import { ReactNode, useCallback, useMemo, useState } from "react";

import {
  getRidePassengerOppositeRideStationId,
  IPassengerInfo,
  IPassengerTooltipInfo,
} from "@ryde/shared";

import { RidePassengerTooltipContent } from "./ride-passenger-tooltip-content";
import {
  getCachedPassengerTooltipInfo,
  mergePassengerTooltipInfo,
  requestPassengerTooltipInfoLoad,
} from "./ride-passenger-tooltip.utils";

export interface IUseRidePassengerTooltipContentArgs {
  passenger: IPassengerInfo;
  companionLabel?: string;
  currentStationId?: number;
  rideSiteId?: number | null;
  rideSiteName?: string;
}

export const useRidePassengerTooltipContent = ({
  passenger,
  companionLabel,
  currentStationId,
  rideSiteId,
  rideSiteName,
}: IUseRidePassengerTooltipContentArgs): {
  content: ReactNode;
  handleTooltipOpenChange: (isOpen: boolean) => void;
} => {
  const { id: passengerId, from, to } = passenger;

  const oppositeRideStationId = useMemo(
    () => getRidePassengerOppositeRideStationId({ from, to }, currentStationId),
    [currentStationId, from, to],
  );

  const [loadedInfo, setLoadedInfo] = useState<
    IPassengerTooltipInfo | undefined
  >(() => getCachedPassengerTooltipInfo(passengerId));

  const handleTooltipOpenChange = useCallback(
    (isOpen: boolean): void => {
      if (!isOpen) {
        return;
      }

      requestPassengerTooltipInfoLoad({
        passengerId,
        onLoaded: info => {
          if (info.id !== passengerId) {
            return;
          }

          setLoadedInfo(info);
        },
      });
    },
    [passengerId],
  );

  const tooltipInfo = useMemo(
    () => mergePassengerTooltipInfo({ loadedInfo, passenger }),
    [loadedInfo, passenger],
  );

  const content = useMemo(
    () => (
      <RidePassengerTooltipContent
        info={tooltipInfo}
        currentStationId={currentStationId}
        oppositeRideStationId={oppositeRideStationId}
        rideSiteId={rideSiteId}
        rideSiteName={rideSiteName}
        companionLabel={companionLabel}
      />
    ),
    [
      companionLabel,
      currentStationId,
      oppositeRideStationId,
      rideSiteId,
      rideSiteName,
      tooltipInfo,
    ],
  );

  return { content, handleTooltipOpenChange };
};
